<template>
	<div class="ferment-container" style="position: relative; padding-left: 20px; padding-right: 20px;" v-show="!loading">
		<div class="ferment_nav" v-if="!ferment.archived && $route.name == 'ferments.index'">
			<a v-if="fermenter.previous" href="#" @click.prevent="gotoPreviousFermenter" class="left"><i class="fa fa-chevron-left"></i></a>
			<a v-if="fermenter.next" href="#" @click.prevent="gotoNextFermenter" class="right"><i class="fa fa-chevron-right"></i></a>
		</div>

		<div class="row">
			<div class="col-lg-6" v-if="$route.name != 'ferments.what-now'">
				<div class="box">
					<div class="box-header">
						<h2 class="box-title">Ferment Data - {{fermenter.name}}</h2>

						<div class="btn-group pull-right top-download-button" v-if="ferment.id && this.$route.name == 'ferments.index'">
							<button :disabled="simulatorRunning" type="button" class="btn btn-xs btn-success dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
								<i class="fa fa-download"></i>
								Download Report <span class="caret"></span>
							</button>
							<ul class="dropdown-menu">
								<li>
									<a href="#" @click.prevent="download('excel')">
										<i class="fa fa-file-excel-o"></i> Download Excel File
									</a>
								</li>
								<li>
									<a href="#" @click.prevent="download('pdf')">
										<i class="fa fa-file-pdf-o"></i> Download PDF File
									</a>
								</li>
								<li>
									<a href="#" @click.prevent="download('jpg')">
										<i class="fa fa-file-image-o"></i> Download JPG File
									</a>
								</li>
							</ul>
						</div>
					</div>
					<div class="box-body">

						<form class="form-horizontal">
							<fieldset>
								<div class="form-group">
									<label for="wine_name" class="col-sm-4 col-md-3 control-label">
										{{ simulator.custom_wine_name ? simulator.custom_wine_name : 'Wine Name' }}*
									</label>
									<div class="col-sm-8 col-md-9">
										<input type="text" name="wine_name" v-model="ferment.wine_name" class="form-control">
									</div>
								</div>
								<div class="form-group" v-show="simulator.blend_code">
									<label for="blend_code" class="col-sm-4 col-md-3 control-label">
										{{ simulator.custom_blend_code ? simulator.custom_blend_code : 'Blend Code' }}
									</label>
									<div class="col-sm-8 col-md-9">
										<input type="text" name="blend_code" v-model="ferment.blend_code" class="form-control">
									</div>
								</div>
								<div class="form-group" v-show="simulator.blend_code">
									<label for="batch_code" class="col-sm-4 col-md-3 control-label">
										{{ simulator.custom_batch_code ? simulator.custom_batch_code : 'Batch Code' }}
									</label>
									<div class="col-sm-8 col-md-9">
										<input type="text" name="batch_code" v-model="ferment.batch_code" class="form-control">
									</div>
								</div>

								<input-select
									:select2="true"
									:options="varietalOptions"
									:disabled="haveMultipleVarietals"
									name="varietal_id"
									:vmodel.sync="selectedVarietalOption"
									label="Varietal"
									v-if="!loading">
								</input-select>

								<input-select
									:select2="true"
									:options="regionOptions"
									name="region_id"
									:vmodel.sync="selectedRegionOption"
									label="Region"
									v-if="!loading">
								</input-select>

								<input-select
									:select2="true"
									:options="winemakerOptions"
									name="winemaker_id"
									:vmodel.sync="selectedWinemakerOption"
									label="Winemaker"
									v-if="!loading">
								</input-select>

								<input-date name="start_date" :vmodel.sync="ferment.start_date" label="Date Start*"></input-date>

								<input-select
									:select2="true"
									:options="blendComponentOptions"
									name="blend_component_id"
									label="Blend Components"
									:vmodel.sync="selectedBlendComponent"
									v-if="!loading && isBlendedFerment">
								</input-select>

								<div class="form-group" v-if="havePreviousFermenters">
									<label class="col-sm-4 col-md-3 control-label">Previous Fermenters</label>

									<div class="col-sm-8 col-md-9">
										<ul class="previous-fermenter-list">
											<li v-for="previousFermenter in ferment.previousFermenters.data">{{ previousFermenter.name }}</li>
										</ul>
									</div>
								</div>

								<div class="margin-20"></div>
							</fieldset>

							<fieldset v-if="!isBlendedFerment">
								<legend class="legend-collapse">
									Juice Analysis <a data-toggle="collapse" data-target=".juice-analysis" class="fieldset-collapse"></a>
								</legend>

								<div class="juice-analysis collapse" :class="{'in': ! collapsed('juice-analysis')}">
									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.baume"
										:spinner="true"
										:postfix="units.sugarUnit()"
										name="baume"
										label="Sugar">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.yan"
										:spinner="true"
										postfix="mg/L"
										name="yan"
										label="YAN*">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.ph"
										:spinner="true"
										name="ph"
										label="pH">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.ta"
										:spinner="true"
										postfix="g/L"
										name="ta"
										label="TA">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.fs02"
										:spinner="true"
										postfix="mg/L"
										name="fs02"
										label="FS0<sub>2</sub>">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.ts02"
										:spinner="true"
										postfix="mg/L"
										name="ts02"
										label="TS0<sub>2</sub>">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.gf"
										:spinner="true"
										postfix="g/L"
										name="gf"
										label="G/F">
									</input-text>

									<input-text
										:vmodel.sync="ferment.juiceAnalysis.data.ntu"
										:spinner="true"
										name="ntu"
										label="NTU">
									</input-text>
								</div>
							</fieldset>

							<fieldset v-if="!isBlendedFerment">
								<legend class="legend-collapse">
									Ferment Setup <a data-toggle="collapse" data-target=".ferment-setup" class="fieldset-collapse" ></a>
								</legend>

								<div class="ferment-setup collapse" :class="{'in': ! collapsed('ferment-setup')}">
									<div class="row">
										<div class="col-md-6">
											<div class="form-group">
												<input-toggle
													name="type"
													:vmodel.sync="ferment.type"
													on="Red"
													off="White"
													label="Ferment Type">
												</input-toggle>
											</div>
										</div>
									</div>

									<div class="row">
										<div class="col-md-12">
											<input-select
												:select2="true"
												:options="profileOptions"
												name="profile"
												:vmodel.sync="profile"
												label="Ferment Profile"
												v-if="!loading">
											</input-select>
										</div>
										<div class="col-md-12">
											<input-text
												:vmodel.sync="ferment.duration"
												:spinner="true"
												postfix="Days"
												name="duration"
												label="Desired Duration*">
											</input-text>
										</div>
									</div>

									<div class="row">
										<div class="col-md-4 col-md-offset-1">
											<div class="form-group">
												<input-toggle
													name="type"
													:vmodel.sync="ferment.climate"
													on="Warm"
													off="Cool"
													off-style="primary"
													label="Climate">
												</input-toggle>
											</div>
										</div>
										<div class="col-md-5">
											<input-toggle
												name="type"
												:vmodel.sync="ferment.style"
												on="Inoculate"
												on-style="default"
												off="Wild"
												off-style="info"
												label="Ferment Style">
											</input-toggle>
										</div>
									</div>

									<div class="row">
										<div class="col-md-6 col-md-offset-6">
											<input-select
												:select2="true"
												:options="yeastTypeOptions"
												name="yeast_type_id"
												:vmodel.sync="ferment.yeast_type_id"
												label="Yeast Types"
												v-if="!loading">
											</input-select>
										</div>
									</div>

									<div class="margin-20"></div>

									<input-text
										:vmodel.sync="ferment.volume"
										:spinner="true"
										postfix="T"
										name="volume"
										label="Ferment Volume*">
									</input-text>

									<input-text
										:vmodel.sync="ferment.total_water_added"
										:spinner="true"
										postfix="L"
										name="water_added"
										label="Total Water Added">
									</input-text>

									<div class="row">
										<div class="col-sm-offet-4 col-sm-4 col-md-offset-3 col-md-4">
											Calculated Sugar: {{{ calculateBaume() }}}
										</div>
										<div class="col-sm-4 col-md-5">
											Calculated YAN: {{{ calculateYan() }}}

											<a class="pull-right fa fa-question-circle popover-help" style="margin-top:5px"
												tabindex="0"
												role="button"
												data-placement="left"
												data-html="true"
												data-trigger="focus"
												data-container="body"
												title="Help"
												data-content="
												<small>
													The initial Be and YAN from the juice analysis are recalculated to account for dilution associated with any additions, etc. at the beginning of ferment. To ensure accurate ferment conditions, be sure to include all initial water additions above, and then throughout the ferment in the data table.
												</small>">
											</a>
										</div>
									</div>

									<div class="margin-20"></div>

									<div class="form-group">
										<div class="col-sm-4 col-md-3">
											<a class="pull-right fa fa-question-circle popover-help" style="margin-left: 10px; margin-top: 10px"
												tabindex="0"
												role="button"
												data-placement="left"
												data-html="true"
												data-trigger="focus"
												data-container="body"
												title="Help"
												data-content="
												<small style='margin-bottom: 10px; display: block'>
													If propagating, you can leave this blank and the simulator will figure it out, or you can use the following equation to convert your cell concentration:
												</small>
												<small style='margin-bottom: 10px; display: block'>
													<code>Cell conc (cells/ml) x pitch rate (decimal) x 5x10^-5</code>
												</small>
												<small style='margin-bottom: 10px; display: block'>
													For example:
												</small>
												<small>
													<code>Equivalent dry yeast = 1x10^8 x 0.05 x 5x10^-5 = 250mg/L</code>
												</small>">
											</a>

											<label for="initial_cell_mass" class="control-label pull-right">
												Initial Cell Mass
											</label>
										</div>
										<div class="col-sm-8 col-md-9">
											<input-text
												:vmodel.sync="ferment.initial_cell_mass"
												:spinner="true"
												postfix="mg/L"
												name="initial_cell_mass">
											</input-text>
										</div>
									</div>

									<div class="margin-20"></div>

									<div class="form-group">
										<label for="comments" class="col-sm-4 col-md-3 control-label">Comments</label>
										<div class="col-sm-8 col-md-9">
											<textarea name="comments" v-model="ferment.comments" class="form-control" rows="8"></textarea>
										</div>
									</div>
								</div>
							</fieldset>
						</form>

						<daily-data v-if="measuringFreqInteger"
							:ferment="ferment"
							:daily-data.sync="ferment.dailyData"
							:measuring-freq="ferment.measuring_freq"
							:measuring-freq-integer="measuringFreqInteger"
							:archived="ferment.archived"
							:days-as-dates="true"
							:start-date="ferment.start_date"
							:editing-disabled="ferment.archived && $route.name == 'ferments.index'">
						</daily-data>

						<hr />

						<div class="margin-20"></div>

						<ul class="nav nav-pills nav-justified" v-if="ferment.id && this.$route.name == 'ferments.index'">
							<li>
								<div class="btn-group btn-block">
									<button :disabled="simulatorRunning" type="button" class="btn btn-block btn-sm btn-success dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
										<i class="fa fa-download"></i>
										Download Report <span class="caret"></span>
									</button>
									<ul class="dropdown-menu">
										<li>
											<a href="#" @click.prevent="download('excel')">
												<i class="fa fa-file-excel-o"></i> Download Excel File
											</a>
										</li>
										<li>
											<a href="#" @click.prevent="download('pdf')">
												<i class="fa fa-file-pdf-o"></i> Download PDF File
											</a>
										</li>
										<li>
											<a href="#" @click.prevent="download('jpg')">
												<i class="fa fa-file-image-o"></i> Download JPG File
											</a>
										</li>
									</ul>
								</div>
							</li>
							<li v-if="ferment.archived">
								<a href="#" @click.prevent="download('csv')" class="btn btn-sm btn-sm btn-info">
									<i class="fa fa-external-link-square"></i> Extract Temperature Profile
								</a>
							</li>
							<li v-if="!ferment.archived">
								<a @click="clearFermenter()" class="btn btn-sm btn-sm btn-info">
									<i class="fa fa-minus"></i> Clear Fermenter
								</a>
							</li>
							<li v-if="!ferment.archived">
								<a @click.prevent="archive()" class="btn btn-sm btn-sm btn-info">
									<i class="fa fa-archive"></i> Archive Ferment
								</a>
							</li>
						</ul>
					</div>
				</div>
			</div>

			<div class="col-lg-6">
				<div class="box" :class="{'sticky-box': $route.name != 'ferments.what-now'}">
					<div class="box-header">
						<h2 class="box-title">
							Ferment Graph
						</h2>
						<a href="#" id="graph-full" data-toggle="modal" data-target="#ferment_graph_modal">
							<span class="text">Expand</span> <i class="fa fa-expand"></i>
						</a>
					</div>
					<div class="box-body">
						<ferment-graph v-if="!loading"></ferment-graph>
					</div>
				</div>
			</div>

			<div class="modal modal-fullscreen fade" id="ferment_graph_modal" tabindex="-1" role="dialog" aria-labelledby="actionsTitle" aria-hidden="true">
				<div class="modal-dialog">
					<div class="modal-content">
						<div class="modal-header">
							<button type="button" class="close" data-dismiss="modal"><span aria-hidden="true">&times;</span><span class="sr-only">Close</span></button>
							<h4 class="modal-title" id="actionsTitle">Ferment Graph</h4>
						</div>
						<div class="modal-body">
							<canvas class="page-graph" id="ferment-modal-graph"></canvas>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-default" data-dismiss="modal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<form method="post" id="download-ferment">
				<input type="hidden" name="graph">
			</form>
		</div>
	</div>
</template>

<style>
	.top-download-button {
		margin: 15px 20px 0 0;
	}

	.previous-fermenter-list {
		list-style: none;
		margin: 5px 0 0;
		padding: 0;
	}

	.previous-fermenter-list li {
		font-size: 13px !important;
	}
</style>

<script>
	import { site, loading, fermenter, varietals, regions, yeastTypes, profiles, ferment, simulatorSetup as simulator, simulatorRunning, graphBase64 } from 'vx/getters'
	import { Fermenters as FermenterActions, Settings as SettingActions, Ferments as FermentActions, App as AppActions, Profiles as ProfileActions } from 'vx/actions'
	import FermentGraph from './partials/graph'
	import DailyData from 'components/ferment/daily-data'
	import Alert from 'utils/alert'
	import Notify from 'utils/notify'
	import Http from 'utils/http'
	import moment from 'moment'
	import Vue from 'vue'
	import Router from 'routes'
	import * as types from 'vx/mutation-types'
	import units from 'utils/units'
	import { caluculateMeasuringFreq } from 'utils/ferment'

	export default {
		components: {
			FermentGraph,
			DailyData
		},

		vuex: {
			getters: {
				site,
				loading,
				fermenter,
				varietals,
				regions,
				yeastTypes,
				profiles,
				ferment,
				simulatorRunning,
				graphBase64,
				simulator
			},

			actions: {
				getFermenter: FermenterActions.get,
				resetFermenters: FermenterActions.reset,
				allProfiles: ProfileActions.all,
				allVarietals: SettingActions.allVarietals,
				allRegions: SettingActions.allRegions,
				allYeastTypes: SettingActions.allYeastTypes,
				getFerment: FermentActions.get,
				updateFerment: FermentActions.update,
				storeFerment: FermentActions.store,
				archiveFerment: FermentActions.archive,
				destroyFerment: FermentActions.destroy,
				setTitle: AppActions.title
			}
		},

		events: {
			resetChanges() {
				this.$store.dispatch(types.SET_ACTIVE_FERMENT, this.originalFerment);

				this.originalFerment = $.extend(true, {}, this.ferment);
			},

			clearSimulateTimeout() {
				clearTimeout(this.simulateTimeout);

				this.$broadcast('clearGraphCountdown');
			}
		},

		route: {
			canReuse: false,

			deactivate(){
				$(window).off('hidden.bs.modal');
				$(window).off('shown.bs.modal');

				$(document).off('keydown');

				clearTimeout(this.simulateTimeout);

				this.resetFermenters()

				Notify.clear();
			},

			activate() {
				this.$dispatch('loading');
			},

			data() {
				window.scrollTo(0, 0);

				this.allVarietals().then(() => {


					this.loaded.push('varietals');
				});

				this.allRegions({ country_id: this.simulator && this.simulator.country_id }).then(() => {
					this.regionOptions = this.select2Transformer(this.regions)

					this.loaded.push('regions');
				});

				this.allYeastTypes().then(() => {
					this.yeastTypeOptions = this.select2Transformer(this.yeastTypes)

					this.loaded.push('yeast-types');
				});

				// If the ferment ID parameter in the route is 0 then we are going to be loading
				// the current ferment for this fermenter.
				if (this.$route.params.fermentId == 0) {
					this.getFermenter(this.$route.params.fermenterId, this.site.id, ['tankGroup', 'currentFerment.blendComponents.fermenter', 'currentFerment.blendComponents.dailyData', 'currentFerment.juiceAnalysis', 'currentFerment.dailyData.action', 'currentFerment.dailyData.analysis']).then(() => {
						if (! this.fermenter.currentFerment) {
							this.isEmptyFermenter = true;
						}

						this.$store.dispatch(types.SET_ACTIVE_FERMENT, this.isEmptyFermenter ? this.getFreshFerment() : this.fermenter.currentFerment.data);

						this.tankGroup = this.fermenter.tankGroup.data;

						this.updateMeasuringFrequency();

						this.loaded.push('fermenter');
					}, () => {
						this.$router.go({ name: 'dashboard' });

						Notify.error('You do not have permission to see that ferment.');
					});

				// If the ID is not zero then we'll load the ferment ID and the required
				// dependencies by tracing back through the ferment.
				} else {
					this.getFerment(this.site.id, this.$route.params.fermenterId, this.$route.params.fermentId, [
						'fermenter.tankGroup',
						'blendComponents.fermenter',
						'blendComponents.dailyData',
						'previousFermenters',
						'juiceAnalysis',
						'dailyData.action',
						'dailyData.analysis'
					]).then(() => {
						this.$store.dispatch(types.SET_ACTIVE_FERMENTER, this.ferment.fermenter.data);

						this.updateMeasuringFrequency();

						this.loaded.push('ferment');
					}, () => {
						this.$router.go({ name: 'dashboard' });

						Notify.error('You do not have permission to see that ferment.');
					});
				}
			}
		},

		data() {
			return {
				profile: '',
				updateTimeout: null,
				simulateTimeout: null,
				originalFerment: {},
				loaded: [],
				tankGroup: {},
				isEmptyFermenter: false,
				yeastTypeOptions: [],
				profileOptions: [],
				recentlyCreated: false,
				units,
				measuringFreqInteger: null,
				selectedBlendComponent: null
			}
		},

		computed: {
			blendComponentOptions () {
				if (this.isBlendedFerment) {
					return this.select2Transformer(_.map(this.ferment.blendComponents.data, ferment => {
						return { id: ferment.id, name: `${ferment.wine_name} ex ${ferment.fermenter.data.name}` }
					}))
				}
			},

			winemakerOptions() {
				if (this.simulator) {
					let winemakers = _.clone(this.simulator.winemakers)

					if (this.haveMultipleWinemakers) {
						winemakers.unshift({ id: 'various', name: 'Various' })
					}

					return this.select2Transformer(winemakers)
				}
			},

			selectedWinemakerOption: {
				get () {
					if (this.haveMultipleWinemakers && this.ferment.winemaker_id == null) {
						return 'various'
					} else {
						return this.ferment.winemaker_id
					}
				},

				set (value) {
					if (this.haveMultipleWinemakers && value === 'various') {
						this.ferment.winemaker_id = null
					} else {
						this.ferment.winemaker_id = value
					}
				}
			},

			selectedVarietalOption: {
				get () {
					if (this.haveMultipleVarietals && this.ferment.varietal_id == null) {
						return 'blend'
					} else {
						return this.ferment.varietal_id
					}
				},

				set (value) {
					if (this.haveMultipleVarietals && value === 'blend') {
						this.ferment.varietal_id = null
					} else {
						this.ferment.varietal_id = value
					}
				}
			},

			selectedRegionOption: {
				get () {
					if (this.haveMultipleRegions && this.ferment.region_id == null) {
						return 'blend'
					} else {
						return this.ferment.region_id
					}
				},

				set (value) {
					if (this.haveMultipleRegions && value === 'blend') {
						this.ferment.region_id = null
					} else {
						this.ferment.region_id = value
					}
				}
			},

			varietalOptions () {
				let varietals = _.clone(this.varietals)

				if (this.haveMultipleVarietals) {
					varietals.unshift({ id: 'blend', name: 'Blend' })
				}

				return this.select2Transformer(varietals)
			},

			regionOptions () {
				let regions = _.clone(this.regions)

				if (this.haveMultipleRegions) {
					regions.unshift({ id: 'blend', name: 'Blend' })
				}

				return this.select2Transformer(regions)
			},

			haveMultipleVarietals () {
				return this.ferment &&
					this.ferment.blendComponents &&
					_.uniqBy(this.ferment.blendComponents.data, 'varietal_id').length > 1
			},

			haveMultipleRegions () {
				return this.ferment &&
					this.ferment.blendComponents &&
					_.uniqBy(this.ferment.blendComponents.data, 'region_id').length > 1
			},

			haveMultipleWinemakers () {
				return this.ferment &&
					this.ferment.blendComponents &&
					_.uniqBy(this.ferment.blendComponents.data, 'winemaker_id').length > 1
			},

			isBlendedFerment () {
				return this.ferment &&
					this.ferment.blendComponents &&
					this.ferment.blendComponents.data.length > 0
			},

			havePreviousFermenters () {
				return this.ferment &&
					this.ferment.previousFermenters &&
					this.ferment.previousFermenters.data.length > 0
			}
		},

		watch: {
			selectedBlendComponent () {
				if (this.selectedBlendComponent) {
					let ferment = this.ferment.blendComponents.data.find(ferment => ferment.id == this.selectedBlendComponent)

					this.$router.go({
						name: 'ferments.index',
						params: {
							fermenterId: ferment.fermenter.data.id,
							fermentId: ferment.id
						}
					})
				}
			},

			simulator(val, oldVal) {
				if (val.country_id && !oldVal.country_id) {
					this.allRegions({ country_id: val.country_id }).then(() => {
						this.regionOptions = this.select2Transformer(this.regions)

						this.loaded.push('regions');
					});
				}
			},

			profile() {
				// If the profile is changed we first need to see if we have an active ferment.
				// If the fermenter isn't empty then it's assumed to be active, if this is the
				// case we need to confirm with the user that they wish to change the profile
				// during the middle of a ferment and that only daily data for the following
				// day will be changed.
				if (! this.isEmptyFermenter) {
					Alert.custom({
						title: 'Profile change during ferment!',
						text: 'You are about to change your ferment profile during a ferment. This will change the desired duration and replace daily data from the next day onwards. Are you sure you want to do this?',
						type: 'warning',
						showCancelButton: true,
						confirmButtonColor: '#d33',
						cancelButtonColor: '#3085d6',
						confirmButtonText: 'Yes, change profile.'
					}).then(() => {
						this.handleProfileSwitch(this.profile);
					}, () => {
						//
					});
				} else {
					this.handleProfileSwitch(this.profile);
				}
			},

			loaded() {
				if (this.loaded.length == 4) {
					this.$dispatch('loaded');

					Vue.nextTick(this.boot);
				}
			}
		},

		ready() {
			this.$on('forceUpdateData', this.save)
		},

		methods: {
			/**
			 * Transform an array to select 2 options fields
			 *
			 * @param  {Array} arr
			 * @param  {String} valueProp
			 * @param  {String} nameProp
			 * @return {Array}
			 */
			select2Transformer(arr, valueProp = 'id', nameProp = 'name') {
				if (!Array.isArray(arr)) {
					return []
				}

				return arr.map(el => ({ name: el[nameProp], value: el[valueProp] }))
			},

			boot() {
				// Clone the ferment so we can reset any dirty changes we make if we are running a
				// what if analysis on the ferment.
				this.originalFerment = $.extend(true, {}, this.ferment);

				this.$broadcast('initialize');

				$('.ferment_nav').sticky({ topSpacing: 30 });
				$('.sticky-box').sticky({ topSpacing: 30 });

				$('.ferment-setup, .juice-analysis').on('show.bs.collapse', event => {
					this.toggleCollapse(event, false);
				});

				$('.ferment-setup, .juice-analysis').on('hide.bs.collapse', event => {
					this.toggleCollapse(event, true);
				});

				$(document).on('keydown', event => {
				    if (event.which === 8 && ! $(event.target).is('input, textarea, select')) {
				        event.preventDefault();
				    }
				});

				$('#ferment_graph_modal').on('show.bs.modal', () => {
					this.$broadcast('buildGraph', 'ferment-modal-graph');
				});

				if (this.fermenter.tankGroup) {
					this.setTitle(`${this.fermenter.tankGroup.data.name} - ${this.fermenter.name}`);
				}

				this.volumeToggle();

				this.scrollToStoredState();

				this.$broadcast('reloadGraph');

				this.disableFields();

				this.updateYeastType();

				this.updateInitialUnits();

				let updateTimeoutSeconds = (this.$route.name == 'ferments.index' ? 1250 : 2000);

				this.$watch('ferment', (value, old) => {
					// When the ferment changes we need to disable the ferment type based on whether or not
					// any daily data has been entered.
					Vue.nextTick(() => {
						$('#type').prop('disabled', this.hasDailyData());
					});

					if (this.ferment.wine_name == '' || ! this.ferment.wine_name || value.id != old.id || this.recentlyCreated) {
						this.recentlyCreated = false;

						return;
					}

					clearTimeout(this.updateTimeout);

					this.updateTimeout = setTimeout(() => {
						this.saveOrCreate();
					}, updateTimeoutSeconds);
				}, { deep: true });

				this.$watch('ferment.type', this.updateMeasuringFrequency);

				this.$watch('ferment.type', this.loadProfiles);

				this.$watch('ferment.style', this.updateYeastType);

				this.loadProfiles();
			},

			loadProfiles() {
				let wasDisabled = $('#profile').prop('disabled') || false

				$('#profile').prop('disabled', true);

				this.allProfiles(this.site.id, { type: this.ferment.type ? 'red' : 'white' }).then(() => {
					this.profileOptions = this.profiles.map(profile => {
						return { name: profile.name, value: profile.id }
					});

					if (this.loaded.indexOf('profiles') == -1) {
						this.loaded.push('profiles');
					}

					$('#profile').prop('disabled', wasDisabled);
				});
			},

			updateInitialUnits() {
				this.ferment.dailyData.data.forEach(day => {
					day.temp = units.celsiusToFahrenheit(day.temp);
					day.baume = units.baumeToBrix(day.baume);
				});

				if (this.ferment.juiceAnalysis) {
					this.ferment.juiceAnalysis.data.baume = units.baumeToBrix(this.ferment.juiceAnalysis.data.baume);
				}
			},

			/**
			 * Update the yeast type disabled property based on the ferment style.
			 *
			 * If it's a wild ferment then they are unable to select a yeast type.
			 *
			 * @return void
			 */
			updateYeastType() {
				if (! this.ferment.style) {
					$('#yeast_type_id').val('').trigger('change').prop('disabled', true);
				} else {
					$('#yeast_type_id').prop('disabled', false);
				}
			},

			/**
			 * Update the ferments measuring frequency based on simulator settings and ferment type.
			 *
			 * This is triggered when the ferment type is changed.
			 *
			 * @return void
			 */
			updateMeasuringFrequency() {
				if (! this.ferment.measuring_freq) {
					this.ferment.measuring_freq = 1.0;
					this.measuringFreqInteger = 1;
				}

				// First, we'll check to see if the ferment is red. If it is, we'll then check to
				// see if the measuring frequency is 1.0 (or once per day) and if the simulators
				// red sampling frequency is set to twice per day (0.5). We'll then do the same
				// for when the ferments measuring frequency is twice per day.
				if (this.ferment.type) {
					/*
					if (this.ferment.measuring_freq == 1.0 && this.simulator.red_sampling_freq) {
						this.ferment.measuring_freq = 1.0 / this.simulator.red_sampling_freq;
					} else if (this.ferment.measuring_freq == 0.5 && ! this.simulator.red_sampling_freq) {
						this.ferment.measuring_freq = 1.0;
					}
					*/

					this.ferment.measuring_freq = caluculateMeasuringFreq(this.simulator.red_sampling_freq);
					this.measuringFreqInteger = this.simulator.red_sampling_freq;

				// The same applies if the ferment is white. We'll check the measuring frequency of
				// the ferment and what the simulators white sampling frequency is and then
				// reverse it if we need to.
				} else {
					/*
					if (this.ferment.measuring_freq == 1.0 && this.simulator.white_sampling_freq) {
						this.ferment.measuring_freq = 0.5;
					} else if (this.ferment.measuring_freq == 0.5 && ! this.simulator.white_sampling_freq) {
						this.ferment.measuring_freq = 1.0;
					}
					*/

					this.ferment.measuring_freq = caluculateMeasuringFreq(this.simulator.white_sampling_freq);
					this.measuringFreqInteger = this.simulator.white_sampling_freq;
				}

				this.ferment.measuring_freq_integer = this.measuringFreqInteger
			},

			/**
			 * Calculate the baume for initial calculation.
			 *
			 * This baume is used as an indicator based on the juice analysis and any dilution. This is
			 * merely a heads up for the user.
			 *
			 * @return float
			 */
			calculateBaume() {
				let sugarOffset = 30;

				if (this.ferment.climate) {
					sugarOffset = 40;
				} else {
					sugarOffset = 25;
				}

				if (! this.ferment.juiceAnalysis || this.ferment.juiceAnalysis.data.baume == null || ! parseFloat(this.ferment.volume) || this.ferment.total_water_added == null) {
					return 'n/a';
				}

				let dilution = parseFloat(this.ferment.volume) / (parseFloat(this.ferment.volume) + (parseFloat(this.ferment.total_water_added) / 1000));

				return units.baumeToBrix(((units.brixToBaume(this.ferment.juiceAnalysis.data.baume) - 0.0181) * dilution + (dilution - 1) * sugarOffset / 16.63 + 0.0181)).toFixed(2)+' '+units.sugarUnit();
			},

			/**
			 * Calculate the YAN for initial calculation.
			 *
			 * Similar to baume this is merely used as an indicator.
			 *
			 * @return float
			 */
			calculateYan() {
				if (! this.ferment.juiceAnalysis || this.ferment.juiceAnalysis.data.yan == null || this.ferment.volume == null || this.ferment.total_water_added == null) {
					return 'n/a';
				}

				let dilution = parseFloat(this.ferment.volume) / (parseFloat(this.ferment.volume) + (parseFloat(this.ferment.total_water_added) / 1000));

				return (parseFloat(this.ferment.juiceAnalysis.data.yan) * dilution).toFixed(2)+' mg/L';
			},

			/**
			 * Disable the appropriate fields based on the route, the ferments status, and
			 * whether or not the ferment has daily data.
			 *
			 * @return void
			 */
			disableFields(){
				if (this.isBlendedFerment) {
					$('select[name=varietal_id]').prop('disabled', true)
					$('select[name=region_id]').prop('disabled', true)
				}

				if (this.ferment.archived && this.$route.name == 'ferments.index') {
					$('.form-horizontal input, textarea, select:not([name=blend_component_id])').prop('disabled', true);

					$('.input-group a').addClass('disabled');
				}

				// If we are running a what if or a what now we want to prevent the user
				// from changing certain fields, so we'll go through and disable
				// all the inputs that should not be editable.
				if (this.$route.name == 'ferments.what-if') {
					$([
							{ name: 'wine_name', type: 'input' }, { name: 'blend_code', type: 'input' }, { name: 'batch_code', type: 'input' },
							{ name: 'varietal_id', type: 'select' }, { name: 'region_id', type: 'select' }, { name: 'start_date', type: 'input' },
							{ name: 'baume', type: 'input' }, { name: 'yan', type: 'input' }, { name: 'ph', type: 'input' }, { name: 'ta', type: 'input' },
							{ name: 'fs02', type: 'input' }, { name: 'ts02', type: 'input' }, { name: 'gf', type: 'input' }, { name: 'ntu', type: 'input' },
							{ name: 'profile', type: 'select' }
					].map(input => `${input.type}[name=${input.name}]`).join(', ')).prop('disabled', true);

					if (this.ferment.archived){
						$(".juice-analysis input, .ferment-setup input, .ferment-setup select, textarea").prop('disabled', false);
					}
				}

				// If the ferment has daily data then we will disable the ferment type
				// checkbox as they should not be changing this mid-ferment.
				if (this.hasDailyData()) {
					$('#type').prop('disabled', true);
				}
			},

			/**
			 * Handle the switch of a ferment profile.
			 *
			 * @param int id
			 *
			 * @return void
			 */
			handleProfileSwitch(id) {
				let profile = _.find(this.profiles, profile => {
					return profile.id == id;
				});

				if (this.hasDailyData()) {
					// If the ferment has daily data already then we need to first confirm that the
					// profile type is the same as the ferment type. If they are trying to select
					// a profile that is RED and the ferment is WHITE then we need to alert
					// them that this is not possible.
					if (profile.type != this.ferment.type) {
						Alert.error('Unable to use profile as the profile type does not match the ferment type.');

						return;
					}
				}

				this.ferment.type = (profile.type == 1 ? true : false);
				this.ferment.duration = profile.duration;

				// So now if the ferment does have daily data we only want to replace the data
				// after the current day. To find this day we need to get the difference
				// between the current date and the start date. This should give us the
				// day number that we need to look for in the daily data. We add 1 to
				// this difference in days because we want the next day.
				if (this.hasDailyData()) {
					let dayDifference = moment().diff(moment(this.ferment.start_date, 'DD/MM/YYYY hh:mm A'), 'days') + 1;

					// If the profile is a red then we need to multiply the day difference by
					// 2 as we have 2 measurements per day.
					if (this.ferment.measuring_freq == 0.5) {
						dayDifference *= 2;
					}

					// If the profile has less daily data than the difference in days then we
					// essentially have no data to add, so we'll simply return here to
					// abort the rest of this function.
					if (profile.dailyData.data.length < dayDifference) {
						return;
					}

					// We'll start our loop at the day difference we have calculated and we'll
					// loop through the remaining number of days in the daily data and simply
					// replace the ferment daily data with the profile daily data.
					for (let i = dayDifference; i < profile.dailyData.data.length; ++i) {
						if (this.ferment.dailyData.data[i]) {
							this.ferment.dailyData.data[i].action = profile.dailyData.data[i].action;
							this.ferment.dailyData.data[i].analysis = profile.dailyData.data[i].analysis;
							this.ferment.dailyData.data[i].temp = profile.dailyData.data[i].temp;
						} else {
							Vue.set(this.ferment.dailyData.data, i, profile.dailyData.data[i]);
						}
					}
				} else {
					this.ferment.dailyData = $.extend({}, this.ferment.dailyData, profile.dailyData);
				}
			},

			/**
			 * Determine if the ferment has daily data. This is handled by the "isBlankRow"
			 * event on the "daily-data" component.
			 *
			 * @return bool
			 */
			hasDailyData() {
				if (this.ferment.dailyData.data.length == 1) {
					let blank = false;

					this.$broadcast('isBlankRow', this.ferment.dailyData.data[0], () => {
						blank = true;
					});

					return ! blank;
				}

				return this.ferment.dailyData.data.length > 0;
			},

			gotoPreviousFermenter() {
				this.storeScrolledState();

				Router.go({ name: 'ferments.index', params: { fermenterId: this.fermenter.previous, fermentId: 0 }});
			},

			gotoNextFermenter() {
				this.storeScrolledState();

				Router.go({ name: 'ferments.index', params: { fermenterId: this.fermenter.next, fermentId: 0 }});
			},

			/**
			 * Scroll the window to the stored state for the given ferment.
			 *
			 * @return void
			 */
			scrollToStoredState() {
				let scrolledState = JSON.parse(localStorage.getItem('scrolledState'));

				if (! scrolledState || scrolledState.top === undefined || scrolledState.left === undefined) return;

				window.scrollTo(scrolledState.left, scrolledState.top);
			},

			/**
			 * Store the scrolled state when switching between ferments.
			 *
			 * @return void
			 */
			storeScrolledState() {
				let doc = document.documentElement,
					left = (window.pageXOffset || doc.scrollLeft) - (doc.clientLeft || 0),
					top = (window.pageYOffset || doc.scrollTop)  - (doc.clientTop || 0),
					scrolledState = JSON.parse(localStorage.getItem('scrolledState'));

				if (! scrolledState) {
					scrolledState = {};
				}

				scrolledState.top = top;
				scrolledState.left = left;

				localStorage.setItem('scrolledState', JSON.stringify(scrolledState));
			},

			/**
			 * Get a fresh ferment object with the required pre-filled data.
			 *
			 * @return object
			 */
			getFreshFerment() {
				return {
					id: null,
					wine_name: '',
					type: false,
					measuring_freq: caluculateMeasuringFreq(this.simulator.white_sampling_freq),
					style: true,
					climate: false,
					duration: null,
					start_date: moment().format('DD/MM/YYYY hh:mm A'),
					juiceAnalysis: {
						data: {}
					},
					dailyData: {
						data: []
					}
				};
			},

			/**
			 * Toggle the postfix for the ferment volume based on the ferment type.
			 *
			 * @return {[type]}
			 */
			volumeToggle() {
				$('#type').change(event => {
					if ($(event.target).prop('checked')){
						$('#volume').parent().find('.input-group-addon').html('T');
					} else {
						$('#volume').parent().find('.input-group-addon').html('kL');
					}
				}).trigger('change');
			},

			/**
			 * Reload the graph after X amount of seconds with a display.
			 *
			 * @param bool force
			 * @param int  seconds
			 *
			 * @return void
			 */
			reloadGraph(force = true, seconds = 10) {
				clearTimeout(this.simulateTimeout);

				this.$broadcast('reloadGraphCountdown', seconds);

				if (this.isBlendedFerment) {
					this.$broadcast('reloadGraph', force);

					return
				}

				this.simulateTimeout = setTimeout(() => {
					this.$broadcast('reloadGraph', force);
				}, seconds * 1000);
			},

			/**
			 * Clear the fermenter.
			 *
			 * @return void
			 */
			clearFermenter() {
				let self = this;

				Alert.delete({
					showLoaderOnConfirm: true,
					preConfirm() {
						return self.destroyFerment(self.site.id, self.fermenter.id, self.ferment.id);
					}
				}).then(() => {
					this.$broadcast('destroyGraph');
					this.$broadcast('clearDailyData');

					this.$store.dispatch(types.SET_ACTIVE_FERMENT, this.getFreshFerment());

					this.$router.go({ name: 'ferments.index', params: { fermenterId: this.fermenter.id, fermentId: 0 }});

					Alert.success({ text: 'Ferment has been deleted.' }).then(() => {}, () => {});
				}, () => {});
			},

			/**
			 * Archive the ferment.
			 *
			 * @return {[type]}
			 */
			archive() {
				let self = this;

				Alert.confirm({
					title: 'Archive Ferment',
					text: 'Are you sure you want to archive this ferment?',
					showLoaderOnConfirm: true,
					preConfirm() {
						return self.archiveFerment(self.site.id, self.fermenter.id, self.ferment.id);
					}
				}).then(() => {
					this.$broadcast('clearGraph');
					this.$broadcast('clearDailyData');

					this.$store.dispatch(types.SET_ACTIVE_FERMENT, this.getFreshFerment());

					this.$router.go({ name: 'ferments.index', params: { fermenterId: this.fermenter.id, fermentId: 0 }});

					Alert.success({ text: 'Ferment has been archived.' }).then(() => {}, () => {});

				}, () => {});
			},

			/**
			 * Save or create the ferment.
			 *
			 * @return {[type]}
			 */
			saveOrCreate() {
				this.updateMeasuringFrequency();

				// If we are on the what if or what now pages we'll simply re-run the simulation
				// be reloading the graph, because we are sending data this should not cache
				// the simulation.
				if (this.$route.name == 'ferments.what-if' || this.$route.name == 'ferments.what-now') {
					this.reloadGraph(false, 5);

					return;
				}

				// Do not allow saving of an archived ferment.
				if (this.ferment.archived) return;

				if (this.ferment.id == null) {
					this.create().then(this.reloadGraph);
				} else {
					this.save().then(this.reloadGraph);
				}
			},

			/**
			 * Create the ferment.
			 *
			 * @return Promise
			 */
			create() {
				return this.storeFerment(this.site.id, this.fermenter.id, this.getSaveOrCreateData()).then(response => {
					this.recentlyCreated = true;

					this.getFerment(this.site.id, this.fermenter.id, response.body.data.id, ['fermenter.tankGroup', 'juiceAnalysis', 'dailyData.action', 'dailyData.analysis']).then(response => {
						Notify.success('New ferment added.');
					});
				});
			},

			/**
			 * Update the ferment.
			 *
			 * @return Promise
			 */
			save() {
				return this.updateFerment(this.site.id, this.fermenter.id, this.getSaveOrCreateData()).then(response => {
					Notify.success('Ferment updated.');
				});
			},

			/**
			 * Get the save or create data.
			 *
			 * @return {[type]}
			 */
			getSaveOrCreateData() {
				let data = $.extend(true, {}, this.ferment);

				data.start_date = moment(data.start_date, 'DD/MM/YYYY hh:mm A').format('YYYY-MM-DD HH:mm:ss');

				// Update the units to convert them to celsius and baume as that is what
				// they are being saved in the database as.
				data.dailyData.data.forEach(day => {
					day.temp = units.fahrenheitToCelsius(day.temp);
					day.baume = units.brixToBaume(day.baume);
				});

				if (data.juiceAnalysis) {
					data.juiceAnalysis.data.baume = units.brixToBaume(data.juiceAnalysis.data.baume);
				}

				return data;
			},

			/**
			 * Determine if the collapsable group should be collapsed or not.
			 *
			 * @param string group
			 *
			 * @return bool
			 */
			collapsed(group) {
				// If the fermenter is empty then regardless of the state that is stored
				// the group will not be collapsed.
				if (this.isEmptyFermenter) {
					return false;
				}

				// Otherwise we'll pull the state of this group for this fermenter from
				// the local storage for this user.
				let collapseState = JSON.parse(localStorage.getItem('collapseState'));

				if (! collapseState || ! collapseState[this.fermenter.id] || ! collapseState[this.fermenter.id][group]) {
					return false;
				}

				return collapseState[this.fermenter.id][group];
			},

			/**
			 * Toggle the collapse state of a collapsable group.
			 *
			 * @param object event
			 * @param bool   collapsed
			 *
			 * @return {[type]}
			 */
			toggleCollapse(event, collapsed) {
				let group = $(event.target).is('.ferment-setup') ? 'ferment-setup' : 'juice-analysis',
					collapseState = JSON.parse(localStorage.getItem('collapseState'));

				// If we have no collapse state we'll create a new object. And if we have no records for the
				// current fermenter we'll also create a new object for it, then we'll store the state
				// of the collapsed group, true if collapsed and false if not collapsed.
				if (! collapseState) {
					collapseState = {};
				}

				if (! collapseState[this.fermenter.id]) {
					collapseState[this.fermenter.id] = {};
				}

				collapseState[this.fermenter.id][group] = collapsed;

				localStorage.setItem('collapseState', JSON.stringify(collapseState));
			},

			/**
			 * Download the ferment as a given format.
			 *
			 * @param string format
			 *
			 * @return void
			 */
			download(format) {
				let path = Http.buildPath(`ferments/${this.ferment.id}/export-${format}`);

				// Grab the snapshot of the canvas so we can send that along to the server.
				this.$broadcast('generateBase64');

				if (! this.graphBase64) {
					Notify.error('Unable to download report, please try again later.');

					return;
				}

				// Update the forms action and the hidden graph input with the generated
				// base64 of the graph then trigger the submission.
				$('#download-ferment')
					.find('input[name=graph]').val(this.graphBase64)
					.parent().attr('action', path).submit();
			},
		}
	};
</script>



// WEBPACK FOOTER //
// index.vue?41562c56